import { Websocket, WebsocketBuilder, WebsocketEvents } from "websocket-ts";
import { Roarr as log } from "roarr";

import type Server from "../server";
import { Message } from "../message";
import { Timer } from "../timer";

type OnMessageHandler = (instance: Websocket, ev: MessageEvent) => any;

export default class WebsocketServer implements Server {
    private websocket: Websocket;
    private server: string;
    private pingTimer: Timer;

    public constructor(server: string) {
        this.server = server;
    }

    public createConnection = async () => {
        return new Promise<void>((resolve, reject) => {
            const onOpen = (i: Websocket, ev: Event) => {
                ws.removeEventListener(WebsocketEvents.open, onOpen);
                ws.removeEventListener(WebsocketEvents.close, onClose);
                ws.removeEventListener(WebsocketEvents.error, onError);

                this.pingTimer = new Timer(async () => {
                    log.debug(`Pinging ${this.server}...`);

                    const pingMessage = new Message("ping");
                    this.sendMessage(pingMessage);

                    await this.awaitMessage("pong");

                    log.debug(`Received pong back!`);
                }, 30 * 1000);
                this.pingTimer.start();

                this.websocket = ws;
                resolve();
            };

            const onClose = (i: Websocket, ev: CloseEvent) => {
                if (this.pingTimer) this.pingTimer.stop();
                reject("Connection closed");
            };

            const onError = (i: Websocket, ev: Event) => {
                if (this.pingTimer) this.pingTimer.stop();
                reject("Connection errored");
            };

            let url = `ws://${this.server}`;
            if (location.protocol === "https:") {
                url = `wss://${this.server}`;
            }

            const ws = new WebsocketBuilder(url)
                .onOpen(onOpen)
                .onClose(onClose)
                .onError(onError)
                .build();
        });
    };

    public closeConnection = () => {
        this.pingTimer.stop();
        this.websocket.close();
    };

    public sendMessage = (msg: Message) => {
        this.pingTimer.reset();
        this.websocket.send(msg.toString());
    };

    public awaitMessage = (messageCommand?: string) => {
        return new Promise<Message>((resolve, reject) => {
            const listener: OnMessageHandler = (i, ev) => {
                const message = Message.parse(ev.data);
                if (messageCommand && message.Command !== messageCommand) {
                    return;
                }

                this.websocket.removeEventListener(
                    WebsocketEvents.message,
                    listener
                );
                this.pingTimer.reset();

                resolve(message);
            };

            this.websocket.addEventListener(WebsocketEvents.message, listener);
        });
    };

    public subscribeToMessage = (
        messageCommand: string,
        handler: (messagePayload: any) => void
    ) => {
        const listener: OnMessageHandler = (i, ev) => {
            this.pingTimer.reset();
            const msg = Message.parse(ev.data);
            if (msg.Command === messageCommand) handler(msg.Parameters);
        };

        this.websocket.addEventListener(WebsocketEvents.message, listener);

        return () => {
            this.websocket.removeEventListener(
                WebsocketEvents.message,
                listener
            );
        };
    };
}
